import { supabase } from './supabase/client';
import {
  Cohort,
  CohortWithDetails,
  CreateCohortDTO,
  UpdateCohortDTO,
  CohortFilters,
} from '../types/cohort.types';

const COHORTS_TABLE = 'cohorts';
const TEACHER_COHORTS_TABLE = 'teacher_cohorts';
const STUDENT_COHORTS_TABLE = 'student_cohort_enrollments';

/**
 * Service for managing cohorts, teacher cohort assignments, and student cohort enrollments.
 */
async function syncCohortStudentProfileGrades(cohortId: string, studentIds: string[]): Promise<void> {
  const { data: cohortData } = await supabase
    .from(COHORTS_TABLE)
    .select('id, class_id, classes(id, name)')
    .eq('id', cohortId)
    .single();

  const classObj: any = cohortData?.classes;
  const targetGradeName = classObj?.name || '';
  if (!targetGradeName) return;

  const gradeMatch = targetGradeName.match(/\d+/);
  const formattedGrade = gradeMatch ? gradeMatch[0] : targetGradeName;

  await supabase
    .from('students')
    .update({ grade: formattedGrade })
    .in('id', studentIds);
}

/**
 * Whether a user works in a cohort: as an assigned teacher, or as a Banker
 * enrolled in it. Only staff-scoped callers use this, so enrolled plain
 * students never reach it.
 */
export function isCohortMember(cohort: CohortWithDetails, identifier: string): boolean {
  const target = identifier.toLowerCase();
  const isTeacher = (cohort.teachers || []).some(
    (t) =>
      t.userId?.toLowerCase() === target ||
      t.teacherId?.toLowerCase() === target ||
      t.id?.toLowerCase() === target ||
      t.email?.toLowerCase() === target
  );
  if (isTeacher) return true;
  return (cohort.students || []).some((s) => s.userId?.toLowerCase() === target || s.email?.toLowerCase() === target);
}

export const cohortService = {
  /**
   * Get cohorts with details (class name, assigned teachers, student counts)
   */
  async getCohorts(filters: CohortFilters = {}): Promise<CohortWithDetails[]> {
    try {
      let query = supabase
        .from(COHORTS_TABLE)
        .select(`
          *,
          classes (
            id,
            name
          ),
          teacher_cohorts (
            id,
            teacher_id,
            teachers (
              id,
              userid,
              users (
                id,
                firstname,
                lastname,
                email
              )
            )
          ),
          student_cohort_enrollments (
            id,
            student_id,
            students (
              id,
              grade,
              balance,
              userid,
              users (
                id,
                firstname,
                lastname,
                email
              )
            )
          )
        `)
        .is('deletedat', null)
        .order('createdat', { ascending: false });

      if (filters.classId) {
        query = query.eq('class_id', filters.classId);
      }

      if (filters.search) {
        query = query.ilike('name', `%${filters.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      if (!data) return [];

      // Format data into CohortWithDetails
      let results: CohortWithDetails[] = data.map((item: any) => {
        const teachers = (item.teacher_cohorts || [])
          .filter((tc: any) => tc && tc.teachers && (!tc.teachers.users || !tc.teachers.users.deletedat))
          .map((tc: any) => {
            const t = tc.teachers;
            return {
              id: tc.id,
              teacherId: t.id,
              userId: t.userid || t.users?.id || '',
              firstname: t.users?.firstname || '',
              lastname: t.users?.lastname || '',
              email: t.users?.email || '',
              subject: t.subject || '',
            };
          });

        const students = (item.student_cohort_enrollments || [])
          .filter((sc: any) => sc && sc.students && sc.students.users && !sc.students.users.deletedat)
          .map((sc: any) => {
            const s = sc.students;
            return {
              id: sc.id,
              studentId: s.id,
              userId: s.userid || s.users?.id || '',
              firstname: s.users?.firstname || '',
              lastname: s.users?.lastname || '',
              email: s.users?.email || '',
              grade: s.grade || '',
              balance: s.balance || 0,
            };
          });

        return {
          id: item.id,
          class_id: item.class_id,
          name: item.name,
          description: item.description,
          createdat: item.createdat,
          updatedat: item.updatedat,
          deletedat: item.deletedat,
          className: item.classes?.name || '',
          teachers,
          students,
          studentCount: students.length,
        };
      });

      // Filter by teacherUserId or teacherId if teacher role filter applies
      const targetTeacherIdentifier = filters.teacherUserId || filters.teacherId;
      if (targetTeacherIdentifier) {
        results = results.filter((cohort) => isCohortMember(cohort, targetTeacherIdentifier));
      }

      return results;
    } catch (error) {
      console.error('[cohortService.getCohorts]:', error);
      throw error;
    }
  },

  /**
   * Get a single cohort by ID
   */
  async getCohortById(id: string): Promise<CohortWithDetails | null> {
    try {
      const cohorts = await this.getCohorts();
      return cohorts.find((c) => c.id === id) || null;
    } catch (error) {
      console.error('[cohortService.getCohortById]:', error);
      throw error;
    }
  },

  /**
   * Create a new cohort under a class
   */
  async createCohort(dto: CreateCohortDTO): Promise<Cohort> {
    try {
      const { data, error } = await supabase
        .from(COHORTS_TABLE)
        .insert({
          class_id: dto.class_id,
          name: dto.name,
          description: dto.description || null,
        })
        .select()
        .single();

      if (error) throw error;

      // Assign initial teachers if provided
      if (dto.teacher_ids && dto.teacher_ids.length > 0) {
        await this.assignTeachersToCohort(data.id, dto.teacher_ids);
      }

      return data as Cohort;
    } catch (error) {
      console.error('[cohortService.createCohort]:', error);
      throw error;
    }
  },

  /**
   * Update cohort metadata and teacher assignments
   */
  async updateCohort(id: string, dto: UpdateCohortDTO): Promise<Cohort> {
    try {
      const updateData: Partial<Cohort> = {
        updatedat: new Date().toISOString(),
      };
      if (dto.class_id !== undefined) updateData.class_id = dto.class_id;
      if (dto.name !== undefined) updateData.name = dto.name;
      if (dto.description !== undefined) updateData.description = dto.description;

      const { data, error } = await supabase
        .from(COHORTS_TABLE)
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      if (dto.teacher_ids !== undefined) {
        await this.assignTeachersToCohort(id, dto.teacher_ids);
      }

      return data as Cohort;
    } catch (error) {
      console.error('[cohortService.updateCohort]:', error);
      throw error;
    }
  },

  /**
   * Soft delete a cohort
   */
  async deleteCohort(id: string): Promise<void> {
    try {
      // 1. Clean up active student & teacher assignments so scholars become Unassigned
      await supabase.from(STUDENT_COHORTS_TABLE).delete().eq('cohort_id', id);
      await supabase.from(TEACHER_COHORTS_TABLE).delete().eq('cohort_id', id);

      // 2. Soft delete the cohort record
      const { error } = await supabase
        .from(COHORTS_TABLE)
        .update({ deletedat: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('[cohortService.deleteCohort]:', error);
      throw error;
    }
  },

  /**
   * Assign list of teachers to a cohort (replaces current assignments)
   */
  async assignTeachersToCohort(cohortId: string, teacherIds: string[]): Promise<void> {
    try {
      // Remove existing teacher cohort assignments for this cohort
      const { error: deleteError } = await supabase
        .from(TEACHER_COHORTS_TABLE)
        .delete()
        .eq('cohort_id', cohortId);

      if (deleteError) throw deleteError;

      if (teacherIds.length === 0) return;

      // Insert new assignments
      const inserts = teacherIds.map((teacherId) => ({
        cohort_id: cohortId,
        teacher_id: teacherId,
      }));

      const { error: insertError } = await supabase.from(TEACHER_COHORTS_TABLE).insert(inserts);
      if (insertError) throw insertError;
    } catch (error) {
      console.error('[cohortService.assignTeachersToCohort]:', error);
      throw error;
    }
  },

  /**
   * Enroll students into a cohort (avoids duplicates)
   */
  async enrollStudentsInCohort(cohortId: string, studentIds: string[]): Promise<void> {
    try {
      if (studentIds.length === 0) return;

      // Fetch existing enrollments
      const { data: existing, error: fetchError } = await supabase
        .from(STUDENT_COHORTS_TABLE)
        .select('student_id')
        .eq('cohort_id', cohortId)
        .in('student_id', studentIds);

      if (fetchError) throw fetchError;

      const existingSet = new Set((existing || []).map((e) => e.student_id));
      const newStudentIds = studentIds.filter((id) => !existingSet.has(id));

      if (newStudentIds.length > 0) {
        const inserts = newStudentIds.map((student_id) => ({
          cohort_id: cohortId,
          student_id,
        }));
        const { error: insertError } = await supabase.from(STUDENT_COHORTS_TABLE).insert(inserts);
        if (insertError) throw insertError;
      }
    } catch (error) {
      console.error('[cohortService.enrollStudentsInCohort]:', error);
      throw error;
    }
  },

  /**
   * Sync scholar enrollments for a cohort (replaces current roster selection)
   */
  async syncStudentEnrollments(
    cohortId: string,
    studentIds: string[],
    syncProfileGrade: boolean = false
  ): Promise<void> {
    try {
      // 1. Remove selected students from any previous cohorts so section transfers are clean
      if (studentIds.length > 0) {
        await supabase
          .from(STUDENT_COHORTS_TABLE)
          .delete()
          .in('student_id', studentIds)
          .neq('cohort_id', cohortId);
      }

      // 2. Remove current enrollments for this target cohort
      const { error: deleteError } = await supabase
        .from(STUDENT_COHORTS_TABLE)
        .delete()
        .eq('cohort_id', cohortId);

      if (deleteError) throw deleteError;

      if (studentIds.length === 0) return;

      // 3. Insert new enrollments
      const inserts = studentIds.map((student_id) => ({
        cohort_id: cohortId,
        student_id,
      }));

      const { error: insertError } = await supabase.from(STUDENT_COHORTS_TABLE).insert(inserts);
      if (insertError) throw insertError;

      // 4. Update student profile grade if authorized
      if (syncProfileGrade && studentIds.length > 0) {
        await syncCohortStudentProfileGrades(cohortId, studentIds);
      }
    } catch (error) {
      console.error('[cohortService.syncStudentEnrollments]:', error);
      throw error;
    }
  },

  /**
   * Unenroll students from a cohort
   */
  async unenrollStudentsFromCohort(cohortId: string, studentIds: string[]): Promise<void> {
    try {
      if (studentIds.length === 0) return;
      const { error } = await supabase
        .from(STUDENT_COHORTS_TABLE)
        .delete()
        .eq('cohort_id', cohortId)
        .in('student_id', studentIds);

      if (error) throw error;
    } catch (error) {
      console.error('[cohortService.unenrollStudentsFromCohort]:', error);
      throw error;
    }
  },

  /**
   * Move a student from one cohort to another (e.g. 7A -> 7B or 6A -> 7A)
   * Keeps student balance & activity history completely intact!
   */
  async moveStudentCohort(studentId: string, fromCohortId: string, toCohortId: string): Promise<void> {
    try {
      if (fromCohortId) {
        await this.unenrollStudentsFromCohort(fromCohortId, [studentId]);
      }
      if (toCohortId) {
        await this.enrollStudentsInCohort(toCohortId, [studentId]);
      }
    } catch (error) {
      console.error('[cohortService.moveStudentCohort]:', error);
      throw error;
    }
  },

  /**
   * Move multiple students from one cohort to another in batch
   */
  async moveStudentsCohort(studentIds: string[], fromCohortId: string, toCohortId: string): Promise<void> {
    try {
      if (!studentIds || studentIds.length === 0) return;
      if (fromCohortId) {
        await this.unenrollStudentsFromCohort(fromCohortId, studentIds);
      }
      if (toCohortId) {
        await this.enrollStudentsInCohort(toCohortId, studentIds);
      }
    } catch (error) {
      console.error('[cohortService.moveStudentsCohort]:', error);
      throw error;
    }
  },

  /**
   * Get cohorts assigned to a teacher by teacher ID
   */
  async getCohortsByTeacherId(teacherId: string): Promise<CohortWithDetails[]> {
    try {
      const { data, error } = await supabase
        .from(TEACHER_COHORTS_TABLE)
        .select('cohort_id')
        .eq('teacher_id', teacherId);

      if (error) throw error;
      if (!data || data.length === 0) return [];

      const cohortIds = data.map((d) => d.cohort_id);
      const allCohorts = await this.getCohorts();
      return allCohorts.filter((c) => cohortIds.includes(c.id));
    } catch (error) {
      console.error('[cohortService.getCohortsByTeacherId]:', error);
      throw error;
    }
  },
};
